import { Flex, Typography, message } from "antd"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { dateTimeFormat } from "../../../utils/utils"
import { nbaDestination } from "../../notifications/contentDestination"
import {
  formatFact,
  toneBackground,
  toneColor
} from "../../notifications/formatFact"
import type { ContentFact } from "../notifications-content"
import { neutral } from "../tokens"
import { WidgetFrame } from "../WidgetFrame"
import type {
  ActivityFeedItem,
  FeedItem,
  WidgetProps,
  WidgetState
} from "../types"
import { isActivityFeedItem } from "../types"

dayjs.extend(relativeTime)

const { Text } = Typography

// One glyph for every action card, as the reference has it — severity is
// carried by the tile's tint, not by a different silhouette per row.
const SeverityIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width={22}
    height={22}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5M12 16.2h.01" />
  </svg>
)

const VISIBLE_LIMIT = 5
const SHOW_MORE_STEP = 25

/**
 * Next Best Actions owns the only warm surface on Home, so its palette is
 * spelled out here rather than taken from the cool widget tokens. The values
 * mirror `DashboardPage/home-theme`; this file sits in the shared widget
 * library and must not import a container's module.
 */
const NBA = {
  surface: "#FFFFFF",
  border: "#E7EDF3",
  ink: "#253348",
  inkMuted: "#697789",
  /** The attention tile — amber, the "at risk" colour, never red or green. */
  warningBg: "#FDF4E7",
  warningFg: "#D3A04F",
  /** EZBuild blue-teal: the button is a control, not a status. */
  actionText: "#287587",
  actionBorder: "#BBD6DD",
  chipBg: "#EFF3F7",
  chipFg: "#5A6879",
  shadow: "0 1px 2px rgba(37, 51, 72, 0.05)"
} as const

// One vertical list in a single panel — items are separated by a rule, not
// boxed individually inside an already-bordered card.
const NBA_LIST_CLASS = "flex flex-col"
const NBA_RATIONALE_LINES = 3
// The supporting line is what the button acts on, so it sits ~10px under it
// rather than being flushed anywhere by a spacer.
const NBA_ACTION_OFFSET = 10

const NBA_HEADER_STYLE: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  padding: "14px 18px",
  borderBottom: `1px solid ${NBA.border}`
}

const NBA_TITLE_STYLE: React.CSSProperties = {
  fontSize: 19,
  lineHeight: "25px",
  fontWeight: 650,
  color: NBA.ink
}

const REVEAL_STYLE: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: 0,
  border: "none",
  background: "none",
  cursor: "pointer",
  fontSize: 13,
  lineHeight: "18px",
  fontWeight: 600,
  color: NBA.actionText
}

/**
 * Every action tile is amber. Severity still travels on `data-severity` and
 * decides the order the server sends, but the tile is an attention marker,
 * not a status: red is Home's Delayed colour and would read as one more
 * failing project rather than "there is something to do here".
 */
const ACTION_TINT = { bg: NBA.warningBg, fg: NBA.warningFg } as const

const ArrowGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width={14}
    height={14}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M4 12h15M13 6l6 6-6 6" />
  </svg>
)

interface NBASlotProps {
  items: FeedItem[]
  /** The host's layout-editing controls, shown in this card's own header. */
  editChrome?: React.ReactNode
}

const NBASlotImpl = ({ items, editChrome }: NBASlotProps) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [visibleCount, setVisibleCount] = useState(VISIBLE_LIMIT)

  const handleAction = (item: FeedItem) => {
    const target = nbaDestination(
      { ...(item.action?.params ?? {}), kind: item.action?.kind },
      item
    )
    if (target) {
      navigate(target)
      return
    }
    message.info(
      t("portfolio.nba.actionNotAvailable", "Action not available yet")
    )
  }

  // A calm empty state, not a hidden card: "nothing needs your attention" is
  // an answer, and a card that vanishes reads as one that failed to load.
  if (!items.length) {
    return (
      <section
        data-testid="nba-empty"
        style={{
          background: NBA.surface,
          border: `1px solid ${NBA.border}`,
          borderRadius: 10,
          boxShadow: NBA.shadow,
          overflow: "hidden"
        }}
      >
        <div style={NBA_HEADER_STYLE}>
          <h2 style={{ ...NBA_TITLE_STYLE, margin: 0 }}>
            {t("portfolio.nba.title", "Next Best Actions")}
          </h2>
          {editChrome ? (
            <div style={{ marginLeft: "auto" }}>{editChrome}</div>
          ) : null}
        </div>
        <div style={{ padding: "28px 18px", textAlign: "center" }}>
          <p
            style={{
              fontSize: 14,
              lineHeight: "20px",
              fontWeight: 600,
              color: NBA.ink,
              margin: "0 0 4px"
            }}
          >
            {t("portfolio.nba.emptyTitle", "Nothing needs your attention")}
          </p>
          <p
            style={{
              fontSize: 13,
              lineHeight: "19px",
              color: NBA.inkMuted,
              margin: 0
            }}
          >
            {t(
              "portfolio.nba.emptyBody",
              "No overdue milestones, unpaid invoices or follow-ups are waiting."
            )}
          </p>
        </div>
      </section>
    )
  }

  const visibleItems = items.slice(0, visibleCount)
  const hiddenCount = items.length - visibleItems.length
  const canCollapse = hiddenCount === 0 && items.length > VISIBLE_LIMIT

  const reveal = hiddenCount > 0 && (
    <button
      type="button"
      aria-expanded={false}
      onClick={() =>
        setVisibleCount((prev) => Math.min(prev + SHOW_MORE_STEP, items.length))
      }
      style={REVEAL_STYLE}
    >
      {t("portfolio.nba.showMore", "Show more ({{count}})", {
        count: hiddenCount
      })}
      <ArrowGlyph />
    </button>
  )

  const collapse = canCollapse && (
    <button
      type="button"
      aria-expanded={true}
      onClick={() => setVisibleCount(VISIBLE_LIMIT)}
      style={REVEAL_STYLE}
    >
      {t("portfolio.nba.showLess", "Show less")}
    </button>
  )

  return (
    <section
      style={{
        background: NBA.surface,
        border: `1px solid ${NBA.border}`,
        borderRadius: 10,
        boxShadow: NBA.shadow,
        overflow: "hidden"
      }}
    >
      {/* Show more rides the heading rather than the section's bottom edge, so
          on a wide monitor it never ends up a screen away from what it opens. */}
      <div style={{ ...NBA_HEADER_STYLE, flexWrap: "nowrap" }}>
        <h2 style={{ ...NBA_TITLE_STYLE, margin: 0, minWidth: 0 }}>
          {t("portfolio.nba.title", "Next Best Actions")}
        </h2>
        {reveal}
        {collapse}
        {/* `flexShrink: 0` and the auto margin keep the remove control on the
            title's own row: the header wraps, and Show more is wide enough to
            push an unpinned control onto a line of its own. */}
        {editChrome ? (
          <div style={{ marginLeft: "auto", flexShrink: 0 }}>{editChrome}</div>
        ) : null}
      </div>

      <div className={NBA_LIST_CLASS}>
        {visibleItems.map((item, index) => (
          <NBARow
            key={item.id}
            item={item}
            first={index === 0}
            onAct={() => handleAction(item)}
          />
        ))}
      </div>
    </section>
  )
}

const NBARow = ({
  item,
  first,
  onAct
}: {
  item: FeedItem
  first: boolean
  onAct: () => void
}) => {
  const { t } = useTranslation()
  const facts = item.facts ?? []
  const lead = facts.find((fact) => fact.emphasis)
  const supporting = facts.filter((fact) => !fact.emphasis)
  const actionKey =
    typeof item.action?.params?.action === "string"
      ? (item.action.params.action as string)
      : undefined

  return (
    <div
      data-testid={`nba-item-${item.id}`}
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        minWidth: 0,
        padding: "14px 18px",
        borderTop: first ? undefined : `1px solid ${NBA.border}`
      }}
    >
      <div
        role="img"
        aria-label={t(`portfolio.nba.severity.${item.severity}`, item.severity)}
        data-testid="severity-icon"
        data-severity={item.severity}
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          flex: "none",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: ACTION_TINT.bg,
          color: ACTION_TINT.fg
        }}
      >
        <SeverityIcon />
      </div>

      {/* One text column: chip, title, record, facts, then the button.
          Nothing between them grows, so the button cannot drift from its
          text however wide the card gets. */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 4,
          minWidth: 0
        }}
      >
        {item.action?.category && (
          <span
            data-testid="nba-category"
            data-category={item.action.category}
            style={{
              maxWidth: "100%",
              fontSize: 11,
              lineHeight: "16px",
              fontWeight: 600,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              padding: "2px 8px",
              borderRadius: 4,
              background: NBA.chipBg,
              color: NBA.chipFg
            }}
          >
            {t(
              `portfolio.nba.category.${item.action.category}`,
              item.action.category
            )}
          </span>
        )}

        <Text
          strong
          style={{
            fontSize: 15,
            lineHeight: "21px",
            fontWeight: 600,
            color: NBA.ink,
            overflowWrap: "anywhere"
          }}
        >
          {item.title}
        </Text>

        {/* Which record, and whose — the two things a title alone never says. */}
        {(item.record || item.customer) && (
          <Text
            data-testid="nba-record"
            style={{
              fontSize: 13,
              lineHeight: "19px",
              color: NBA.inkMuted,
              overflowWrap: "anywhere"
            }}
          >
            {item.record?.name}
            {item.record && item.customer ? " · " : ""}
            {item.customer}
          </Text>
        )}

        {(item.subject?.label || supporting.length > 0) && (
          <Text
            data-testid="nba-detail"
            style={{
              fontSize: 13,
              lineHeight: "19px",
              color: NBA.inkMuted,
              overflowWrap: "anywhere"
            }}
          >
            {[
              item.subject?.label,
              ...supporting.map((fact) => {
                const { label, value } = formatFact(fact, t)
                return fact.kind === "status" ? value : `${label} ${value}`
              })
            ]
              // A suggestion's subject is its target status, which its
              // status fact names again.
              .filter(
                (part, index, parts) => !!part && parts.indexOf(part) === index
              )
              .join(" · ")}
          </Text>
        )}

        {/* A feed produced before the typed facts still says why. */}
        {facts.length === 0 && item.rationale && (
          <Text
            title={item.rationale}
            style={
              {
                fontSize: 13,
                lineHeight: "19px",
                color: NBA.inkMuted,
                display: "-webkit-box",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: NBA_RATIONALE_LINES,
                overflow: "hidden"
              } as React.CSSProperties
            }
          >
            {item.rationale}
          </Text>
        )}

        {lead && <NBALeadFact fact={lead} />}

        <button
          type="button"
          onClick={onAct}
          data-testid="nba-action"
          data-action={actionKey}
          style={{
            marginTop: NBA_ACTION_OFFSET,
            height: 32,
            padding: "0 14px",
            fontSize: 13,
            lineHeight: "18px",
            fontWeight: 600,
            color: NBA.actionText,
            background: NBA.surface,
            border: `1px solid ${NBA.actionBorder}`,
            borderRadius: 6,
            cursor: "pointer",
            whiteSpace: "nowrap"
          }}
        >
          {/* Never "Take Action": the label names where it goes. A feed with
              no destination named falls back to its kind, which at least
              says what the row is about. */}
          {actionKey
            ? t(
                `notifications.actions.${actionKey}`,
                defaultActionLabel(actionKey)
              )
            : t(
                `portfolio.nba.kindAction.${item.kind}`,
                defaultActionLabel(item.kind)
              )}
        </button>
      </div>
    </div>
  )
}

const NBALeadFact = ({ fact }: { fact: ContentFact }) => {
  const { t } = useTranslation()
  const { label, value } = formatFact(fact, t)
  const tone = fact.tone ?? "warning"
  return (
    <span
      data-testid={`nba-lead-fact-${fact.key}`}
      style={{
        marginTop: 2,
        padding: "4px 10px",
        borderRadius: 6,
        fontSize: 12,
        lineHeight: "16px",
        fontWeight: 600,
        background: toneBackground[tone],
        color: toneColor[tone]
      }}
    >
      {fact.direction
        ? value
        : fact.kind === "duration_days"
          ? `${value} ${label.toLowerCase()}`
          : `${label} ${value}`}
    </span>
  )
}

const defaultActionLabel = (key: string) => {
  const words = key.replace(/_/g, " ")
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export const NBASlot = React.memo(NBASlotImpl)

const ActivityRow = ({ item }: { item: ActivityFeedItem }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const target = item.drill_to
  const absolute = dayjs(item.timestamp)
  const relative = absolute.isValid() ? absolute.fromNow() : item.timestamp
  const tooltip = absolute.isValid()
    ? absolute.format(dateTimeFormat)
    : undefined

  const handleOpen = () => {
    if (target) navigate(target)
  }

  return (
    <Flex
      data-testid={`feed-item-${item.id}`}
      role={target ? "button" : undefined}
      tabIndex={target ? 0 : undefined}
      onClick={target ? handleOpen : undefined}
      onKeyDown={
        target
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleOpen()
              }
            }
          : undefined
      }
      vertical
      gap={2}
      style={{
        padding: "10px 12px",
        border: `1px solid ${neutral.border}`,
        borderRadius: 10,
        background: neutral.surface,
        cursor: target ? "pointer" : "default"
      }}
    >
      <Text strong ellipsis={{ tooltip: item.title }}>
        {item.title}
      </Text>
      <Text
        type="secondary"
        data-testid="feed-timestamp"
        title={tooltip}
        aria-label={t("portfolio.feed.occurredAt", "Occurred at {{time}}", {
          time: tooltip ?? relative
        })}
        style={{ fontSize: 12 }}
      >
        {relative}
      </Text>
    </Flex>
  )
}

const ActivityFeedImpl = ({
  widget,
  payload,
  state,
  onRefresh,
  onExpand
}: WidgetProps) => {
  const { t } = useTranslation()
  const items = (payload?.feed?.items ?? []).filter(isActivityFeedItem)
  const title = widget.config?.title ?? widget.title

  let effectiveState: WidgetState
  if (state === "loading" || state === "error") {
    effectiveState = state
  } else if (!payload?.feed) {
    effectiveState = "loading"
  } else if (items.length === 0) {
    effectiveState = "empty"
  } else {
    effectiveState = state ?? "ready"
  }

  return (
    <WidgetFrame
      title={title}
      state={effectiveState}
      capabilities={widget.capabilities}
      widgetId={widget.id}
      payload={payload}
      emptyText={t("portfolio.feed.empty", "No recent activity")}
      onRefresh={onRefresh}
      onExpand={onExpand}
    >
      <Flex vertical gap={8}>
        {items.map((item) => (
          <ActivityRow key={item.id} item={item} />
        ))}
      </Flex>
    </WidgetFrame>
  )
}

export const ActivityFeed = React.memo(ActivityFeedImpl)

const EMPTY_ITEMS: FeedItem[] = []

export const FeedWidget = (props: WidgetProps) => {
  const items = props.payload?.feed?.items ?? EMPTY_ITEMS
  const { dataSource } = props.widget
  const isNbaFeed =
    dataSource === "nba" ||
    (dataSource == null && items.some((item) => !isActivityFeedItem(item)))
  if (isNbaFeed) {
    return <NBASlot items={items as FeedItem[]} editChrome={props.editChrome} />
  }
  return <ActivityFeed {...props} />
}
